import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import HeaderComponent from './HeaderComponent';


const DocumentUpload = () => {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const userType = locationState?.userType || 'Existing';
  
  
  const [formData, setFormData] = useState({
    // HR Document
    hrDocument: null,
    
    // Passport
    passportDocument: null,
    
    // Police Check
    policeCheckDocument: null,
    policeCheckExpiry: '',
    
    // Working with Children
    workingChildrenDocument: null,
    workingChildrenExpiry: '',
    
    // Visa
    visaDocument: null,
    visaType: '',
    visaExpiry: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [modifiedFields, setModifiedFields] = useState(new Set());
  
  // Input refs
  const hrInputRef = useRef(null);
  const passportInputRef = useRef(null);
  const policeCheckInputRef = useRef(null);
  const workingChildrenInputRef = useRef(null);
  const visaInputRef = useRef(null);

  // Load user data from API after receiving initial data
  useEffect(() => {
    const fetchUserData = async () => {
      
      if (locationState) {
        
        
        try {
          setLoading(true);
          
          // Send the location state data to fetch the complete user data (34 indexes)
          const uploadData = await fetch('/api/validate-login-user-doc', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(locationState?.data)
          });
          
          if (uploadData) {
            const userData = await uploadData.json();
            
            setUserData(userData);
          } else {
            console.error("Failed to fetch complete user data");
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }

    };
    
    fetchUserData();
  }, [locationState]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-300">
        <p>Loading documents uploading hub...</p>
      </div>
    );
  }
  
  const handleBack = async () => {
    
    
    try {
      setIsSubmitting(true);
      const responseRedirect = await fetch('/api/validate-login-user-redirect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData)
      });
      
      if (responseRedirect.ok) {
        const userData2 = await responseRedirect.json();
        
        navigate('/landingprofile', { state: { userType, data: userData2 } });
      } else {
        // Fallback in case the API call fails
        navigate('/landingprofile', { state: { userType, data: userData } });
      }
    } catch (error) {
      console.error("Error returning to profile:", error);
      navigate('/landingprofile', { state: { userType, data: userData } });
    } finally {
      setIsSubmitting(false);
    }

  
  };

  // Carousel navigation
  const nextCard = () => {
    setActiveIndex((prevIndex) => (prevIndex === 4 ? 0 : prevIndex + 1));
  };

  const prevCard = () => {
    setActiveIndex((prevIndex) => (prevIndex === 0 ? 4 : prevIndex - 1));
  };

  // Form handling
  const handleSelectChange = (name, e) => {
    setFormData({ ...formData, [name]: e.target.value });
    setModifiedFields(new Set([...modifiedFields, name]));
    validateField(name, e.target.value);
  };

  const validateField = (name, value) => {
    let errors = { ...formErrors };
    
    if (name === 'visaType' && !value) {
      errors.visaType = 'Visa type is required';
    } else if (name === 'visaType') {
      delete errors.visaType;
    }
    
    if ((name === 'visaExpiry' || name === 'policeCheckExpiry' || name === 'workingChildrenExpiry') && !value) {
      errors[name] = 'Expiry date is required';
    } else if ((name === 'visaExpiry' || name === 'policeCheckExpiry' || name === 'workingChildrenExpiry')) {
      delete errors[name];
    }
    
    setFormErrors(errors);
  };

  const validateFutureDate = (name, e) => {
    const value = e.target.value;
    setFormData({ ...formData, [name]: value });
    setModifiedFields(new Set([...modifiedFields, name]));
    
    const selectedDate = new Date(value);
    const today = new Date();
    
    let errors = { ...formErrors };
    
    if (!value) {
      errors[name] = 'Date is required';
    } else if (selectedDate <= today) {
      errors[name] = 'Date must be in the future';
    } else {
      delete errors[name];
    }
    
    setFormErrors(errors);
  };

  const validatePDFFile = (name, e) => {
    const file = e.target.files[0];
    setModifiedFields(new Set([...modifiedFields, name]));
    
    let errors = { ...formErrors };
    
    if (!file) {
      setFile(name, null);
      errors[name] = 'Document is required';
    } else if (file.type !== 'application/pdf') {
      setFile(name, null);
      errors[name] = 'Only PDF files are allowed';
    } else {
      setFile(name, file);
      delete errors[name];
    }
    
    setFormErrors(errors);
  };

  const setFile = (name, file) => {
    setFormData({ ...formData, [name]: file });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate only the active card's fields
    let errors = {};
    let hasErrors = false;
    
    switch (activeIndex) {
      case 0: // HR Document
        if (!formData.hrDocument) {
          errors.hrDocument = 'HR Document is required';
          hasErrors = true;
        }
        break;
      case 1: // Passport
        if (!formData.passportDocument) {
          errors.passportDocument = 'Passport Document is required';
          hasErrors = true;
        }
        break;
      case 2: // Police Check
        if (!formData.policeCheckDocument) {
          errors.policeCheckDocument = 'Police Check Document is required';
          hasErrors = true;
        }
        if (!formData.policeCheckExpiry) {
          errors.policeCheckExpiry = 'Expiry date is required';
          hasErrors = true;
        }
        break;
      case 3: // Working with Children
        if (!formData.workingChildrenDocument) {
          errors.workingChildrenDocument = 'Working with Children Document is required';
          hasErrors = true;
        }
        if (!formData.workingChildrenExpiry) {
          errors.workingChildrenExpiry = 'Expiry date is required';
          hasErrors = true;
        }
        break;
      case 4: // Visa
        if (!formData.visaDocument) {
          errors.visaDocument = 'Visa Document is required';
          hasErrors = true;
        }
        if (!formData.visaType) {
          errors.visaType = 'Visa type is required';
          hasErrors = true;
        }
        if (!formData.visaExpiry) {
          errors.visaExpiry = 'Expiry date is required';
          hasErrors = true;
        }
        break;
      default:
        // Default case - no validation needed
        break;
    }
    
    if (hasErrors) {
      setFormErrors(errors);
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const formDataToSend = new FormData();
      
      // Only send the data for the active document
      switch (activeIndex) {
        case 0: // HR Document
          if (formData.hrDocument) {
            formDataToSend.append('hrDocument', formData.hrDocument);
            formDataToSend.append('documentType', 'hr');
          }
          break;
        case 1: // Passport
          if (formData.passportDocument) {
            formDataToSend.append('passportDocument', formData.passportDocument);
            formDataToSend.append('documentType', 'passport');
          }
          break;
        case 2: // Police Check
          if (formData.policeCheckDocument) {
            formDataToSend.append('policeCheckDocument', formData.policeCheckDocument);
            formDataToSend.append('policeCheckExpiry', formData.policeCheckExpiry);
            formDataToSend.append('documentType', 'policeCheck');
          }
          break;
        case 3: // Working with Children
          if (formData.workingChildrenDocument) {
            formDataToSend.append('workingChildrenDocument', formData.workingChildrenDocument);
            formDataToSend.append('workingChildrenExpiry', formData.workingChildrenExpiry);
            formDataToSend.append('documentType', 'workingChildren');
          }
          break;
        case 4: // Visa
          if (formData.visaDocument) {
            formDataToSend.append('visaDocument', formData.visaDocument);
            formDataToSend.append('visaExpiry', formData.visaExpiry);
            formDataToSend.append('visaType', formData.visaType);
            formDataToSend.append('documentType', 'visa');
          }
          break;
        default:
          // Default case - no action needed
          break;
      }
      
      // Add user ID and document record ID from the complete user data
      if (userData) {
        // Add user record ID (index 31)
        if (userData[31]) {
          formDataToSend.append('userRecordId', userData[31]);
        }
        
        // Add document record ID (index 32)
        if (userData[32]) {
          formDataToSend.append('documentRecordId', userData[32]);
        }
      }
      
      // Upload just the active document
      const uploadResponse = await fetch('/api/upload/docs', {
        method: 'POST',
        body: formDataToSend
      });
      
      if (!uploadResponse.ok) {
        const error = await uploadResponse.json();
        setFormErrors(error.errors || { general: 'Failed to upload document' });
        setIsSubmitting(false);
        return;
      }
      
      // After successful upload, get updated user data
      const responseRedirect = await fetch('/api/validate-login-user-redirect', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData)
      });
    
      if (responseRedirect.ok) {
      const userData2 = await responseRedirect.json();
      alert("Successful document upload");
      navigate('/landingprofile', { 
          state: { userType, data: userData2 } 
        });
    } else {
      // Fallback in case the API call fails
      alert("Unsuccessful document upload");
     navigate('/landingprofile', { 
        state: { userType, data: userData } 
      });
    }
      
    } catch (error) {
      setFormErrors({ general: 'An error occurred: ' + error.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Card titles
  const cardTitles = [
    'HR Document',
    'Passport',
    'Police Check',
    'Working with Children',
    'Visa Document'
  ];

  return (
    <>
    <HeaderComponent
      title="Document Upload"
      backLabel="Back to Hub"
      onBack={handleBack}
      showSearch={false}
    />
    
    <div className="min-h-screen py-6" style={{ backgroundColor: "#1a1a2e" }}>
      <div className="max-w-4xl mx-auto p-4">
        <h2 className="text-2xl font-bold mb-6" style={{ color: '#22d3ee' }}>Document Upload</h2>
        {userData && (
          <p className="mb-6" style={{ color: '#ffffff' }}>Hello <span className="font-semibold">
            {userData[4] || 'User'}
          </span>, your work status is <span className="font-semibold">{userData[6]}</span>, please scroll to the document type you need to upload,
          all document uploads that you don't see select <span className="font-semibold"> HR Document</span>.</p>
        )}
        
        {/* Carousel - Mobile View Only */}
        <div className="relative mb-8">
          <div className="flex flex-col space-y-3">
            {cardTitles.map((title, index) => (
              <div
                key={index}
                className={`transition-all duration-300 transform ${
                  index === activeIndex 
                    ? 'bg-gray-800 border-2 border-indigo-500 text-white shadow-lg rounded-xl' 
                    : 'bg-white text-gray-800 shadow-md rounded-lg'
                } p-4 flex items-center justify-between cursor-pointer`}
                onClick={() => setActiveIndex(index)}
              >
                <h3 className="text-lg font-medium">{title}</h3>
                {index === activeIndex && (
                  <span className="ml-2 bg-indigo-600 rounded-full p-1 w-6 h-6 flex items-center justify-center">
                    <span className="text-white text-xs">✓</span>
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-6">
          {/* HR Document Form */}
          {activeIndex === 0 && (
            <div className="form-section">
              <h4 className="text-lg font-medium text-gray-700 mb-3">HR Document Upload</h4>
              <div className="space-y-4">
                <div>
                  <input
                    id="hr-document-upload"
                    type="file"
                    name="hrDocument"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => validatePDFFile('hrDocument', e)}
                    ref={hrInputRef}
                  />
                  
                  {!formData.hrDocument ? (
                    <button
                      type="button"
                      onClick={() => hrInputRef.current.click()}
                      className="upload-button flex items-center justify-center px-4 py-2 w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow hover:from-blue-600 hover:to-indigo-700 transition-all duration-200"
                    >
                      <span className="font-medium">Upload HR Document</span>
                    </button>
                  ) : (
                    <div className="file-info p-3 bg-gray-50 rounded-md border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="file-name text-sm font-medium text-gray-700">
                            {formData.hrDocument.name}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFile('hrDocument', null);
                            validatePDFFile('hrDocument', { target: { files: [] } });
                          }}
                          className="text-sm text-red-600 hover:text-red-800 font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {formErrors.hrDocument && modifiedFields && 
                  typeof modifiedFields.has === 'function' && modifiedFields.has('hrDocument') && (
                    <div className="text-sm text-red-600 mt-1">
                      {formErrors.hrDocument}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Navigation buttons */}
              <div className="flex justify-between mt-6">
                <button
                  type="button"
                  onClick={prevCard}
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={nextCard}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}
          
          {/* Passport Form */}
          {activeIndex === 1 && (
            <div className="form-section">
              <h4 className="text-lg font-medium text-gray-700 mb-3">Passport Upload</h4>
              <div className="space-y-4">
                <div>
                  <input
                    id="passport-document-upload"
                    type="file"
                    name="passportDocument"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => validatePDFFile('passportDocument', e)}
                    ref={passportInputRef}
                  />
                  
                  {!formData.passportDocument ? (
                    <button
                      type="button"
                      onClick={() => passportInputRef.current.click()}
                      className="upload-button flex items-center justify-center px-4 py-2 w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow hover:from-blue-600 hover:to-indigo-700 transition-all duration-200"
                    >
                      <span className="font-medium">Upload Passport</span>
                    </button>
                  ) : (
                    <div className="file-info p-3 bg-gray-50 rounded-md border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="file-name text-sm font-medium text-gray-700">
                            {formData.passportDocument.name}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFile('passportDocument', null);
                            validatePDFFile('passportDocument', { target: { files: [] } });
                          }}
                          className="text-sm text-red-600 hover:text-red-800 font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {formErrors.passportDocument && modifiedFields && 
                  typeof modifiedFields.has === 'function' && modifiedFields.has('passportDocument') && (
                    <div className="text-sm text-red-600 mt-1">
                      {formErrors.passportDocument}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Navigation buttons */}
              <div className="flex justify-between mt-6">
                <button
                  type="button"
                  onClick={prevCard}
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={nextCard}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}
          
          {/* Police Check Form */}
          {activeIndex === 2 && (
            <div className="form-section">
              <h4 className="text-lg font-medium text-gray-700 mb-3">Police Check Upload</h4>
              <div className="space-y-4">
                <div>
                  <input
                    id="police-check-document-upload"
                    type="file"
                    name="policeCheckDocument"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => validatePDFFile('policeCheckDocument', e)}
                    ref={policeCheckInputRef}
                  />
                  
                  {!formData.policeCheckDocument ? (
                    <button
                      type="button"
                      onClick={() => policeCheckInputRef.current.click()}
                      className="upload-button flex items-center justify-center px-4 py-2 w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow hover:from-blue-600 hover:to-indigo-700 transition-all duration-200"
                    >
                      <span className="font-medium">Upload Police Check</span>
                    </button>
                  ) : (
                    <div className="file-info p-3 bg-gray-50 rounded-md border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="file-name text-sm font-medium text-gray-700">
                            {formData.policeCheckDocument.name}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFile('policeCheckDocument', null);
                            validatePDFFile('policeCheckDocument', { target: { files: [] } });
                          }}
                          className="text-sm text-red-600 hover:text-red-800 font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {formErrors.policeCheckDocument && modifiedFields && 
                  typeof modifiedFields.has === 'function' && modifiedFields.has('policeCheckDocument') && (
                    <div className="text-sm text-red-600 mt-1">
                      {formErrors.policeCheckDocument}
                    </div>
                  )}
                </div>
                
                <div>
                  <label htmlFor="police-check-expiry" className="block text-sm font-medium text-gray-700 mb-1">
                    Police Check Expiry Date
                  </label>
                  <input
                    type="date"
                    id="police-check-expiry"
                    name="policeCheckExpiry"
                    value={formData.policeCheckExpiry}
                    onChange={(e) => validateFutureDate('policeCheckExpiry', e)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  
                  {formErrors.policeCheckExpiry && modifiedFields && 
                  typeof modifiedFields.has === 'function' && modifiedFields.has('policeCheckExpiry') && (
                    <div className="text-sm text-red-600 mt-1">
                      {formErrors.policeCheckExpiry}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Navigation buttons */}
              <div className="flex justify-between mt-6">
                <button
                  type="button"
                  onClick={prevCard}
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={nextCard}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}
          
          {/* Working with Children Form */}
          {activeIndex === 3 && (
            <div className="form-section">
              <h4 className="text-lg font-medium text-gray-700 mb-3">Working with Children Card Upload</h4>
              <div className="space-y-4">
                <div>
                  <input
                    id="working-children-document-upload"
                    type="file"
                    name="workingChildrenDocument"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => validatePDFFile('workingChildrenDocument', e)}
                    ref={workingChildrenInputRef}
                  />
                  
                  {!formData.workingChildrenDocument ? (
                    <button
                      type="button"
                      onClick={() => workingChildrenInputRef.current.click()}
                      className="upload-button flex items-center justify-center px-4 py-2 w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow hover:from-blue-600 hover:to-indigo-700 transition-all duration-200"
                    >
                      <span className="font-medium">Upload Working with Children Card</span>
                    </button>
                  ) : (
                    <div className="file-info p-3 bg-gray-50 rounded-md border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="file-name text-sm font-medium text-gray-700">
                            {formData.workingChildrenDocument.name}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFile('workingChildrenDocument', null);
                            validatePDFFile('workingChildrenDocument', { target: { files: [] } });
                          }}
                          className="text-sm text-red-600 hover:text-red-800 font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {formErrors.workingChildrenDocument && modifiedFields && 
                  typeof modifiedFields.has === 'function' && modifiedFields.has('workingChildrenDocument') && (
                    <div className="text-sm text-red-600 mt-1">
                      {formErrors.workingChildrenDocument}
                    </div>
                  )}
                </div>
                
                <div>
                  <label htmlFor="working-children-expiry" className="block text-sm font-medium text-gray-700 mb-1">
                    Working with Children Card Expiry Date
                  </label>
                  <input
                    type="date"
                    id="working-children-expiry"
                    name="workingChildrenExpiry"
                    value={formData.workingChildrenExpiry}
                    onChange={(e) => validateFutureDate('workingChildrenExpiry', e)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  
                  {formErrors.workingChildrenExpiry && modifiedFields && 
                  typeof modifiedFields.has === 'function' && modifiedFields.has('workingChildrenExpiry') && (
                    <div className="text-sm text-red-600 mt-1">
                      {formErrors.workingChildrenExpiry}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Navigation buttons */}
              <div className="flex justify-between mt-6">
                <button
                  type="button"
                  onClick={prevCard}
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={nextCard}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}
          
          {/* Visa Form */}
          {activeIndex === 4 && (
            <div className="form-section">
              <h4 className="text-lg font-medium text-gray-700 mb-3">Visa Information</h4>
              <div className="space-y-4">
                <div>
                  <label htmlFor="visa-type" className="block text-sm font-medium text-gray-700 mb-1">
                    Visa Type
                  </label>
                  <select
                    id="visa-type"
                    name="visaType"
                    value={formData.visaType || ''}
                    onChange={(e) => handleSelectChange('visaType', e)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select</option>
                    <option value="Citizen">Citizen</option>
                    <option value="Permanent Residency">Permanent Residency</option>
                    <option value="Student Visa">Student Visa</option>
                    <option value="Bridging Visa">Bridging Visa</option>
                    <option value="Family & Partner Visa">Family & Partner Visa</option>
                    <option value="Visitor Visa">Visitor Visa</option>
                    <option value="Trade Visa">Trade Visa</option>
                    <option value="Refugee Visa">Refugee Visa</option>
                    <option value="Other">Other</option>
                    <option value="Temporary Graduate">Temporary Graduate</option>
                    <option value="Temporary Activity">Temporary Activity</option>
                  </select>
                  
                  {formErrors.visaType && modifiedFields && 
                  typeof modifiedFields.has === 'function' && modifiedFields.has('visaType') && (
                    <div className="text-sm text-red-600 mt-1">
                      {formErrors.visaType}
                    </div>
                  )}
                </div>
                
                <div>
                  <label htmlFor="visa-expiry" className="block text-sm font-medium text-gray-700 mb-1">
                    Visa Expiry Date
                  </label>
                  <input
                    type="date"
                    id="visa-expiry"
                    name="visaExpiry"
                    value={formData.visaExpiry}
                    onChange={(e) => validateFutureDate('visaExpiry', e)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                  
                  {formErrors.visaExpiry && modifiedFields && 
                  typeof modifiedFields.has === 'function' && modifiedFields.has('visaExpiry') && (
                    <div className="text-sm text-red-600 mt-1">
                      {formErrors.visaExpiry}
                    </div>
                  )}
                </div>
                
                <div>
                  <input
                    id="visa-document-upload"
                    type="file"
                    name="visaDocument"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => validatePDFFile('visaDocument', e)}
                    ref={visaInputRef}
                  />
                  
                  {!formData.visaDocument ? (
                    <button
                      type="button"
                      onClick={() => visaInputRef.current.click()}
                      className="upload-button flex items-center justify-center px-4 py-2 w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg shadow hover:from-blue-600 hover:to-indigo-700 transition-all duration-200"
                    >
                      <span className="font-medium">Upload Visa Document</span>
                    </button>
                  ) : (
                    <div className="file-info p-3 bg-gray-50 rounded-md border border-gray-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="file-name text-sm font-medium text-gray-700">
                            {formData.visaDocument.name}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setFile('visaDocument', null);
                            validatePDFFile('visaDocument', { target: { files: [] } });
                          }}
                          className="text-sm text-red-600 hover:text-red-800 font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {formErrors.visaDocument && modifiedFields && 
                  typeof modifiedFields.has === 'function' && modifiedFields.has('visaDocument') && (
                    <div className="text-sm text-red-600 mt-1">
                      {formErrors.visaDocument}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Navigation buttons */}
              <div className="flex justify-between mt-6">
                <button
                  type="button"
                  onClick={prevCard}
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={nextCard}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                >
                  Next
                </button>
              </div>
            </div>
          )}
          
          {/* Submit Button */}
          <div className="mt-6">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Document'}
            </button>
            
            {formErrors.general && (
              <div className="text-sm text-red-600 mt-2">
                {formErrors.general}
              </div>
            )}
          </div>
        </form>
        
      </div>
    </div>
    </>
  );
};

export default DocumentUpload;